import React from 'react';
import { Flex, TextV2, Box, BoxProps } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { Skeleton } from '@shared/components/Skeleton';
import { useHotelUserRating } from '@apps/admin/routes/Planning/routes/Accommodations/hooks/useHotelUserRating';
import { TravelMapBlockType } from '@apps/admin/routes/Planning/routes/Accommodations/Accommodations.types';
import { AccommodationCombinedType } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/components/Accommodations/Accommodation.types';
import { useTransformRatingToCategory } from '@apps/admin/routes/Planning/components/HotelsListV2/hooks/useTransformRatingToCategory';
import { useCompactNumberFormatter } from '@shared/hooks/useCompactNumberFormatter';
import { Maybe } from '@graphql/generated';
export interface HotelArgs
  extends Readonly<{
    type?: TravelMapBlockType | AccommodationCombinedType;
    id?: Maybe<string>;
    itemId?: Maybe<string>;
    joyPlaceId?: Maybe<string>;
    zhId?: Maybe<string>;
    rating?: Maybe<number>;
    ratingCount?: Maybe<number>;
  }> {}

interface HotelRatingV2Props
  extends BoxProps,
    Readonly<{
      hotel: HotelArgs;
      variant?: 'small' | 'large' | 'mobileSmall' | 'leftAligned';
      showRatingCategory?: boolean;
      isSkipQuery?: boolean;
    }> {}

export const HotelRatingV2: React.FC<HotelRatingV2Props> = ({ hotel, variant, showRatingCategory = true, isSkipQuery, ...boxProps }) => {
  const { t } = useTranslation('planningAccommodations');
  const { hotelItem } = t('browse');
  const isSmall = variant === 'small';
  const isMobileSmall = variant === 'mobileSmall';
  const { formatCompactNumber } = useCompactNumberFormatter();
  const { reviewCount, validatedRating, ratingCategory, loading } = useHotelUserRating({
    hotel,
    context: 'admin accommodation details pane user rating',
    skip: isSkipQuery || !!hotel?.rating,
  });

  const hotelRatingCategory = useTransformRatingToCategory(hotel?.rating);
  const ratingCategoryString = hotelRatingCategory || ratingCategory;

  const getFlexDirection = () => {
    if (isMobileSmall) {
      return 'column-reverse';
    }
    if (isSmall) {
      return 'row-reverse';
    }
    return 'row';
  };

  const getRatingBoxSize = () => {
    if (isSmall) {
      return pxToRem(36);
    }
    return pxToRem(40);
  };

  const getReviewCount = () => {
    if (hotel?.ratingCount) {
      return formatCompactNumber(hotel.ratingCount);
    }
    return formatCompactNumber(reviewCount || 0);
  };

  if (loading) {
    return (
      <Flex
        gap={5}
        width="fit-content"
        flexDirection={getFlexDirection()}
        alignItems={isMobileSmall ? 'flex-end' : 'center'}
        {...boxProps}
      >
        <Flex
          flexDirection="column"
          gap={1}
        >
          <Skeleton
            placeholder={
              <Box
                width={pxToRem(80)}
                height={pxToRem(16)}
                backgroundColor="mono3"
              >
                &nbsp;
              </Box>
            }
          />
          <Skeleton
            placeholder={
              <Box
                width={pxToRem(60)}
                height={pxToRem(14)}
                backgroundColor="mono3"
              >
                &nbsp;
              </Box>
            }
          />
        </Flex>
        <Skeleton
          placeholder={
            <Box
              width={getRatingBoxSize()}
              height={getRatingBoxSize()}
              backgroundColor="mono3"
              borderRadius={pxToRem(6)}
            >
              &nbsp;
            </Box>
          }
        />
      </Flex>
    );
  }

  if (!reviewCount && !validatedRating && !hotel.rating && !hotel.ratingCount) {
    return null;
  }

  const rating = hotel?.rating || validatedRating;

  return (
    <Flex
      gap={5}
      width="fit-content"
      flexDirection={getFlexDirection()}
      alignItems={isMobileSmall ? 'flex-end' : 'center'}
      {...boxProps}
    >
      <Flex
        color="white"
        alignItems="center"
        justifyContent="center"
        borderRadius={pxToRem(6)}
        width={getRatingBoxSize()}
        typographyVariant="button1"
        backgroundColor="newUITest1"
        height={getRatingBoxSize()}
      >
        {rating?.toFixed(1)}
      </Flex>
      <Flex
        flexDirection="column"
        gap={1}
        textAlign={isSmall || variant === 'leftAligned' ? 'start' : 'end'}
      >
        {showRatingCategory && (
          <TextV2
            typographyVariant="button1"
            color="mono14"
            whiteSpace="nowrap"
          >
            {ratingCategoryString}
          </TextV2>
        )}
        {(hotel?.ratingCount || reviewCount) && (
          <TextV2
            typographyVariant="label2"
            color="mono10"
            whiteSpace="nowrap"
          >
            {hotelItem.reviews({ reviews: getReviewCount() })}
          </TextV2>
        )}
      </Flex>
    </Flex>
  );
};
